import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { setFileStorage, setFiles, clearFileStorage } from '@Redux/fileStorage/fileStorageActions';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import FileService from '@Helpers/URLHelpers/FileService';
import ErrorHandler from '@Root/ErrorHandler';

export const useReduxFileStorage = () => {

    const dispatch = useDispatch();
    const fileService = new FileService();
    const errorHandler = new ErrorHandler();

    const fileStorage = useSelector((state) => state.fileStorage.currentFileStorage, shallowEqual);
    const currentFiles = useSelector((state) => state.fileStorage.currentFiles, shallowEqual);

    const filesCount = useMemo(() => {
        return currentFiles ? Object.keys(currentFiles).length : 0;
    }, [currentFiles]);

    const fetchFileStorage = useCallback(async (tableName, itemId) => {
        try {
            dispatch(clearFileStorage());
            let response = await fileService.getS3FileStorageByTableTypeAndItemId(tableName, itemId);
            if(!response || response.status >= 400) return;
            response = JSON.parse(response.body);
            const storage = response;
            dispatch(setFileStorage(storage));
            const parsedFiles = (!storage?.files || storage.files === 'NA') ? {} : JSON.parse(storage.files);
            const parsedFileDetails = (!storage?.fileDetails || storage.fileDetails === 'NA') ? {} : JSON.parse(storage.fileDetails);
            const filesObj = { ...parsedFileDetails };
            Object.keys(parsedFiles).forEach((fileName) => {
                const fileId = parsedFiles[fileName];
                if (!filesObj[fileId]) {
                    filesObj[fileId] = {
                        fileName,
                        fileType: 'NA',
                        storeType: 'whatsappFile',
                        createUserId: {
                            fullName: 'System',
                            userName: 'system',
                            id: 0
                        },
                        fileSize: 0,
                        createTimeStamp: -1,
                        active: true,
                        id: fileId
                    };
                }
            });
            dispatch(setFiles(filesObj));
        } catch (err) {
            errorHandler.handleError(err);
        }
    }, [dispatch]);

    const uploadExistingFileInS3ByFileId = useCallback(async (fileData) => {
        try {
            let response = await fileService.getPublicUrlAndUploadExistingFileClobToS3(fileData);
            if(response.status < 400) {
                response = JSON.parse(response.body);
                return response;
            } else {
                SnackbarManager.show(response.error, 'error');
            }
            return null;
        } catch(err) {
            errorHandler.handleError(err);
            return null;
        }
    }, [dispatch]);


    const uploadFileInS3 = useCallback(async (fileData) => {
        try {
            let response = await fileService.addFileToFileStorageAndGenerateUrl(fileData);
            if(response.status < 400) {
                response = JSON.parse(response.body);
                return response;
            } else {
                SnackbarManager.show(response.error, 'error');
            }
            return null;
        } catch(err) {
            errorHandler.handleError(err);
            return null;
        }
    }, [dispatch]);

    const downloadFile = async(url, fileName) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch(err) {
            errorHandler.handleError(err);
        }
    }

    const clearFileStorageValue = useCallback(() => {
        dispatch(clearFileStorage());
    }, [dispatch]);

    return {
        fileStorage,
        currentFiles,
        filesCount,
        fetchFileStorage,
        downloadFileFromUrl: downloadFile,
        uploadFileInS3,
        uploadExistingFileInS3ByFileId,
        clearFileStorage: clearFileStorageValue
    };
};
